import { Controller } from '@hotwired/stimulus'

/**
 * Toggles visibility of pane targets based on a current value.
 *
 * Usage:
 *   %div{ data: { controller: "ui-pane-switch", "ui-pane-switch-current-value": "tab1" } }
 *     = ui "input/segmented", name: :view, options: [...], data: { action: "ui-pane-switch#select" }
 *     %div{ data: { "ui-pane-switch-target": "pane", "ui-pane-switch-pane-value": "tab1" } }
 *     %div{ data: { "ui-pane-switch-target": "pane", "ui-pane-switch-pane-value": "tab2" } }
 */
export default class extends Controller<HTMLElement> {
  static targets = ['pane']
  static values = { current: String }

  declare readonly paneTargets: HTMLElement[]
  declare readonly hasPaneTarget: boolean
  declare currentValue: string

  connect(): void {
    if (this.currentValue) {
      this.#showPane(this.currentValue)
    } else {
      this.#showFirstPane()
    }
  }

  select(e: Event): void {
    const input = e.target as HTMLInputElement | null
    if (!input?.value) return

    this.currentValue = input.value
  }

  currentValueChanged(): void {
    this.#showPane(this.currentValue)
  }

  #showPane(value: string): void {
    this.paneTargets.forEach(pane => {
      const paneValue = pane.dataset.uiPaneSwitchPaneValue ?? ''
      pane.classList.toggle('hidden', paneValue !== value)
    })
  }

  #showFirstPane(): void {
    if (!this.hasPaneTarget) return

    this.paneTargets.forEach((pane, index) => {
      pane.classList.toggle('hidden', index !== 0)
    })
  }
}
